"use client";

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { MoreVertical } from "lucide-react";

/**
 * Single RUNYI contextual-action layer. Anchored to its actual button but rendered
 * outside scrollable cards and nested app screens, so it cannot shift or clip a
 * workspace. No hardcoded per-provider menu positions.
 */
export function EnterpriseContextMenu({
  label, children, title, compact = false, width = 224, controlledOpen, onOpenChange,
}: {
  label: string;
  children: ReactNode;
  title?: string;
  compact?: boolean;
  width?: number;
  controlledOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = useCallback((next: boolean | ((current: boolean) => boolean)) => {
    const value = typeof next === "function" ? next(open) : next;
    if (controlledOpen === undefined) setInternalOpen(value);
    onOpenChange?.(value);
  }, [controlledOpen, onOpenChange, open]);
  const [placement, setPlacement] = useState({ left: 8, top: 56, maxHeight: 240, width });

  const position = useCallback(() => {
    const button = trigger.current;
    if (!button || typeof window === "undefined") return;
    const rect = button.getBoundingClientRect();
    const viewport = window.visualViewport;
    const leftEdge = viewport?.offsetLeft ?? 0;
    const topEdge = viewport?.offsetTop ?? 0;
    const screenWidth = viewport?.width ?? window.innerWidth;
    const screenHeight = viewport?.height ?? window.innerHeight;
    const gutter = 8;
    const menuWidth = Math.min(width, Math.max(160, screenWidth - gutter * 2));
    const left = Math.max(leftEdge + gutter, Math.min(rect.right - menuWidth, leftEdge + screenWidth - menuWidth - gutter));
    const requestedHeight = Math.min(panel.current?.scrollHeight ?? 270, screenHeight - gutter * 2);
    const fitsBelow = topEdge + screenHeight - rect.bottom - gutter >= requestedHeight;
    const available = fitsBelow ? topEdge + screenHeight - rect.bottom - gutter * 2 : rect.top - topEdge - gutter * 2;
    const maxHeight = Math.max(80, Math.min(requestedHeight, available));
    const top = fitsBelow ? rect.bottom + 4 : Math.max(topEdge + gutter, rect.top - maxHeight - 4);
    setPlacement({ left, top, maxHeight, width: menuWidth });
  }, [width]);

  useLayoutEffect(() => {
    if (!open) return;
    position();
  }, [open, position]);

  useEffect(() => {
    if (!open) return;
    const dismiss = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!trigger.current?.contains(target) && !panel.current?.contains(target)) setOpen(false);
    };
    const back = (event: Event) => {
      if (event.defaultPrevented) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      setOpen(false);
    };
    const keydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") { event.preventDefault(); setOpen(false); trigger.current?.focus(); }
    };
    window.addEventListener("runyi:request-back", back, { capture: true });
    document.addEventListener("pointerdown", dismiss, true);
    window.addEventListener("keydown", keydown);
    window.addEventListener("resize", position);
    window.addEventListener("scroll", position, true);
    window.visualViewport?.addEventListener("resize", position);
    window.visualViewport?.addEventListener("scroll", position);
    return () => {
      window.removeEventListener("runyi:request-back", back, { capture: true });
      document.removeEventListener("pointerdown", dismiss, true);
      window.removeEventListener("keydown", keydown);
      window.removeEventListener("resize", position);
      window.removeEventListener("scroll", position, true);
      window.visualViewport?.removeEventListener("resize", position);
      window.visualViewport?.removeEventListener("scroll", position);
    };
  }, [open, position, setOpen]);

  return <>
    <button ref={trigger} type="button" aria-label={label} aria-expanded={open} aria-haspopup="menu"
      onClick={() => setOpen((current) => !current)}
      className={`ml-auto grid shrink-0 place-items-center rounded-xl border border-foreground/10 bg-foreground/[0.025] text-foreground/65 touch-manipulation hover:bg-foreground/[0.07] ${compact ? "size-8" : "size-9"}`}>
      <MoreVertical className={compact ? "size-3.5" : "size-4"} />
    </button>
    {open && typeof document !== "undefined" && createPortal(
      <div ref={panel} role="menu" aria-label={label}
        onClickCapture={(event) => {
          const target = event.target;
          if (target instanceof Element && target.closest("button:not([disabled])")) setOpen(false);
        }}
        style={{ position: "fixed", left: placement.left, top: placement.top,
          width: placement.width, maxHeight: placement.maxHeight, zIndex: 220 }}
        className="max-w-[calc(100vw-1rem)] overflow-x-hidden overflow-y-auto overscroll-contain rounded-xl border border-foreground/10 bg-popover p-1.5 shadow-2xl shadow-black/30">
        {title && <p className="truncate border-b border-foreground/8 px-2.5 py-2 text-[10px] font-semibold uppercase tracking-wider text-foreground/50">{title}</p>}
        {children}
      </div>, document.body) }
  </>;
}
